/**
 * Serviceberichte, Wartungspläne, MagicLinks, Link-Graph und Auditspur.
 *
 * Vier Dinge, die inhaltlich wenig verbindet — aber alle vier sind das
 * *Ergebnis* der Arbeit, die in den anderen Dateien entsteht, und alle vier
 * zeigen auf mehrere von ihnen. Sie in eine Datei zu legen erspart einen
 * Ringschluss zwischen vier Modulen, die sich sonst gegenseitig importieren
 * müssten.
 */

import type {
  Auditeintrag,
  Kante,
  MagicLink,
  Servicebericht,
  Wartungsplan,
} from "./modell.js";
import {
  AUFTRAG,
  BERICHT,
  CHATRAUM,
  DATEI,
  DOKUMENT,
  EINGANG,
  EINSATZ,
  KUNDE,
  MAGICLINK,
  MAIL,
  PERSON,
  TERMIN,
  WARTUNGSPLAN,
} from "./kennungen.js";
import { ref } from "./modell.js";
import { tag, werktag, zeitpunkt } from "./zeit.js";

/**
 * Serviceberichte in allen fünf Zuständen aus `07` §7.16.
 *
 * Der Kühlungsbericht liegt zweimal vor: Version 1 ist `ersetzt`, Version 2
 * ist `veroeffentlicht`. Das ist kein Füllmaterial — „die Freigabe bezieht
 * sich immer auf eine konkrete Version", und ohne zwei Versionen im Bestand
 * lässt sich die Anzeige dieser Regel nicht beurteilen. Die Beziehung
 * zwischen beiden ist `supersedes` im Link-Graphen.
 *
 * `interneNotiz` ist überall dort gefüllt, wo es interessant wird: Der Text
 * darf **nicht** im Kunden-PDF landen. Ein Bestand ohne interne Notizen würde
 * die Trennung nie auf die Probe stellen.
 */
export function berichte(bezugstag: Date): readonly Servicebericht[] {
  return [
    {
      ref: BERICHT.kuehlungV1,
      einsatz: EINSATZ.kuehlungVorwoche,
      auftrag: AUFTRAG.kuehlung,
      version: 1,
      zustand: "ersetzt",
      erstellt: zeitpunkt(bezugstag, -7, 10, 45),
      erstelltVon: PERSON.monteurA,
      taetigkeit:
        "Fehlerspeicher ausgelesen (E9, 14 Einträge). Verflüssiger gereinigt, "
        + "Temperaturfühler PT1000 getauscht. Probelauf 30 Minuten ohne Abschaltung.",
      interneNotiz:
        "Verflüssiger war stark verschmutzt – Kunde reinigt offenbar nicht wie "
        + "vereinbart. Beim nächsten Mal ansprechen. Verdacht auf Platinendefekt bleibt.",
      zeiten: [ref("portal", "worktime", "zt-6901"), ref("portal", "worktime", "zt-6902")],
      material: [ref("portal", "material", "mt-5007")],
      bilder: [DATEI.fotoKuehlungVorher],
      freigegebenVon: PERSON.buero,
      freigegebenAm: zeitpunkt(bezugstag, -6, 9, 0),
      pdf: DATEI.berichtPdfKuehlung,
    },
    {
      // Version 2 — noch im Entwurf, weil der Einsatz von heute noch läuft.
      // Der Monteur hat schon getippt, das Büro hat noch nichts gesehen.
      ref: BERICHT.kuehlungV2,
      einsatz: EINSATZ.kuehlungHeute,
      auftrag: AUFTRAG.kuehlung,
      version: 2,
      zustand: "entwurf",
      erstellt: zeitpunkt(bezugstag, 0, 9, 40),
      erstelltVon: PERSON.monteurA,
      taetigkeit:
        "Steuerplatine ausgebaut, Parameter gesichert. Ersatzplatine eingebaut. "
        + "Probelauf steht noch aus.",
      interneNotiz: "Alte Platine aufheben – Hersteller will sie zur Analyse.",
      zeiten: [ref("portal", "worktime", "zt-7001"), ref("portal", "worktime", "zt-7002")],
      material: [ref("portal", "material", "mt-5001"), ref("portal", "material", "mt-5002")],
      bilder: [DATEI.fotoKuehlungNachher],
      ersetzt: BERICHT.kuehlungV1,
    },
    {
      ref: BERICHT.treppenhaus,
      einsatz: EINSATZ.treppenhausVorwoche,
      auftrag: AUFTRAG.treppenhaus,
      version: 1,
      zustand: "veroeffentlicht",
      erstellt: zeitpunkt(bezugstag, -5, 15, 30),
      erstelltVon: PERSON.monteurB,
      taetigkeit:
        "EG bis 2. OG: neun LED-Wannenleuchten montiert und angeschlossen, drei "
        + "Bewegungsmelder gesetzt und auf 3 Minuten Nachlauf eingestellt.",
      zeiten: [ref("portal", "worktime", "zt-6903"), ref("portal", "worktime", "zt-6904")],
      material: [ref("portal", "material", "mt-5005"), ref("portal", "material", "mt-5006")],
      bilder: [],
      freigegebenVon: PERSON.buero,
      freigegebenAm: zeitpunkt(bezugstag, -4, 10, 30),
      pdf: DATEI.berichtPdfTreppenhaus,
    },
    {
      // Zur Prüfung beim Büro — und genau deshalb zurückgegeben worden.
      ref: BERICHT.hebebuehne,
      einsatz: EINSATZ.hebebuehneVorwoche,
      auftrag: AUFTRAG.hebebuehne,
      version: 1,
      zustand: "zur_pruefung",
      erstellt: zeitpunkt(bezugstag, -4, 16, 15),
      erstelltVon: PERSON.monteurB,
      taetigkeit: "Sichtprüfung Hebebühne 1 durchgeführt, keine Mängel festgestellt.",
      interneNotiz: "Messgerät hatte leeren Akku – Messwerte fehlen, muss nachgeholt werden.",
      zeiten: [ref("portal", "worktime", "zt-6905")],
      material: [],
      bilder: [DATEI.fotoHebebuehne],
    },
    {
      ref: BERICHT.rufanlage,
      einsatz: EINSATZ.rufanlageVorwoche,
      auftrag: AUFTRAG.rufanlage,
      version: 1,
      zustand: "freigegeben",
      erstellt: zeitpunkt(bezugstag, -12, 11, 10),
      erstelltVon: PERSON.monteurB,
      taetigkeit:
        "Rufanlage Station 1: alle 22 Rufknöpfe ausgelöst und quittiert, Akkutest der "
        + "Zentrale bestanden (Notlaufzeit 71 Minuten). Zwei Akkus vorsorglich getauscht.",
      zeiten: [ref("portal", "worktime", "zt-6906")],
      material: [ref("portal", "material", "mt-5010")],
      bilder: [],
      freigegebenVon: PERSON.inhaber,
      freigegebenAm: zeitpunkt(bezugstag, -11, 8, 20),
      unterschrift: { name: "Jörg Pfaff", am: zeitpunkt(bezugstag, -12, 11, 5) },
    },
    {
      ref: BERICHT.netzwerk,
      einsatz: EINSATZ.netzwerkVorwoche,
      auftrag: AUFTRAG.netzwerk,
      version: 1,
      zustand: "veroeffentlicht",
      erstellt: zeitpunkt(bezugstag, -20, 16, 20),
      erstelltVon: PERSON.monteurA,
      taetigkeit:
        "320 m Cat.7 verlegt, 14 Doppeldosen gesetzt, Patchfeld aufgelegt und gemessen. "
        + "Beschriftung des Patchfelds steht noch aus.",
      zeiten: [ref("portal", "worktime", "zt-6907")],
      material: [ref("portal", "material", "mt-5008"), ref("portal", "material", "mt-5009")],
      bilder: [],
      freigegebenVon: PERSON.buero,
      freigegebenAm: zeitpunkt(bezugstag, -19, 9, 0),
    },
  ];
}

/**
 * Wartungspläne — `07` §7.16 nennt Vorlage, Intervall, Bezugsdatum, Vorlauf,
 * Zuständigkeit, nächste Fälligkeit und Status.
 *
 * Einer ist pausiert. Das Büro darf einen Plan „pausieren, einmalig
 * verschieben, eine Fälligkeit überspringen oder nachholen" — vier Aktionen,
 * die eine Oberfläche anbieten muss und die an einem rein aktiven Bestand
 * nicht prüfbar sind.
 */
export function wartungsplaene(bezugstag: Date): readonly Wartungsplan[] {
  return [
    {
      ref: WARTUNGSPLAN.seniorenheimRufanlage,
      kunde: KUNDE.seniorenheim,
      titel: "Rufanlage – halbjährliche Wartung",
      intervallMonate: 6,
      bezugsdatum: tag(bezugstag, -190),
      vorlaufTage: 21,
      zustaendig: PERSON.inhaber,
      naechsteFaelligkeit: tag(bezugstag, werktag(bezugstag, 1)),
      status: "aktiv",
      letzterEinsatz: EINSATZ.rufanlageVorwoche,
    },
    {
      ref: WARTUNGSPLAN.autohausHebebuehne,
      kunde: KUNDE.autohaus,
      titel: "Hebebühnen 1 und 2 – Jahresprüfung Elektrik",
      intervallMonate: 12,
      bezugsdatum: tag(bezugstag, -360),
      vorlaufTage: 30,
      zustaendig: PERSON.inhaber,
      naechsteFaelligkeit: tag(bezugstag, 5),
      status: "aktiv",
      letzterEinsatz: EINSATZ.hebebuehneVorwoche,
    },
    {
      ref: WARTUNGSPLAN.gemeindePruefung,
      kunde: KUNDE.gemeinde,
      titel: "Ortsveränderliche Betriebsmittel – jährliche Prüfung",
      intervallMonate: 12,
      bezugsdatum: tag(bezugstag, -340),
      vorlaufTage: 45,
      zustaendig: PERSON.inhaber,
      naechsteFaelligkeit: tag(bezugstag, 25),
      status: "pausiert",
    },
  ];
}

/**
 * MagicLinks — `04` §4.9, sieben Tage gültig.
 *
 * Drei Zustände, weil das Dashboard nach `07` §7.4 fragt, „welche MagicLinks
 * bald ablaufen": einer frisch, einer läuft in zwei Tagen ab, einer ist
 * widerrufen. Ohne den ablaufenden hätte die Karte nie etwas anzuzeigen.
 */
export function magiclinks(bezugstag: Date): readonly MagicLink[] {
  return [
    {
      ref: MAGICLINK.berichtBaeckerei,
      ziel: DATEI.berichtPdfKuehlung,
      empfaenger: "b.hufnagel@baeckerei-hufnagel-demo.de",
      erstellt: zeitpunkt(bezugstag, -6, 9, 5),
      laeuftAb: zeitpunkt(bezugstag, 1, 9, 5),
      widerrufen: false,
      zugriffe: 3,
      zweck: "Servicebericht 2481-1 zum Abruf",
    },
    {
      ref: MAGICLINK.abnahmeAutohaus,
      ziel: DOKUMENT.pruefprotokoll2025,
      empfaenger: "r.merkle@autohaus-merkle-demo.de",
      erstellt: zeitpunkt(bezugstag, -1, 14, 0),
      laeuftAb: zeitpunkt(bezugstag, 6, 14, 0),
      widerrufen: false,
      zugriffe: 0,
      zweck: "Prüfprotokoll Vorjahr zur Gegenprüfung",
    },
    {
      ref: MAGICLINK.angebotMetallbau,
      ziel: DOKUMENT.lieferschein,
      empfaenger: "info@metallbau-grasser-demo.de",
      erstellt: zeitpunkt(bezugstag, -5, 10, 0),
      laeuftAb: zeitpunkt(bezugstag, 2, 10, 0),
      widerrufen: true,
      zugriffe: 1,
      zweck: "Lieferschein – widerrufen, weil an die falsche Adresse versandt",
    },
  ];
}

/**
 * Der Link-Graph nach `04` §4.3.
 *
 * **Er enthält keine Inhalte**, nur typisierte Beziehungen zwischen
 * ResourceRefs. Das ist der Punkt, an dem aus einer Sammlung von Diensten ein
 * Produkt wird: Unter „Auftrag 2481" erscheinen nach `07` §7.1 Aufgaben,
 * Dateien, Dokumente, Mails, Chatraum und Termin gemeinsam — und zwar nicht,
 * weil sie kopiert wurden, sondern weil hier Kanten liegen.
 *
 * Der Bestand nutzt alle elf Beziehungstypen aus `04` §4.3. Das ist kein
 * Selbstzweck: Ein Typ, den kein Datensatz benutzt, ist ein Typ, dessen
 * Darstellung nie jemand gebaut hat.
 */
export function kanten(bezugstag: Date): readonly Kante[] {
  const k = (
    source: Kante["source"],
    relation: Kante["relation"],
    target: Kante["target"],
    createdBy: Kante["createdBy"],
    tageVersatz: number,
  ): Kante => ({
    source,
    relation,
    target,
    createdBy,
    createdAt: zeitpunkt(bezugstag, tageVersatz, 10, 0),
  });

  return [
    // Aufträge an Kunden
    k(AUFTRAG.kuehlung, "belongs_to_customer", KUNDE.baeckerei, PERSON.buero, -9),
    k(AUFTRAG.treppenhaus, "belongs_to_customer", KUNDE.hausverwaltung, PERSON.buero, -21),
    k(AUFTRAG.stoerung, "belongs_to_customer", KUNDE.baeckerei, PERSON.buero, 0),
    k(AUFTRAG.wallbox, "belongs_to_customer", KUNDE.privat, PERSON.buero, -14),

    // Dateien und Dokumente am Auftrag
    k(DATEI.schaltplanKuehlung, "belongs_to_job", AUFTRAG.kuehlung, PERSON.buero, -9),
    k(DATEI.fotoKuehlungVorher, "belongs_to_job", AUFTRAG.kuehlung, PERSON.monteurA, -7),
    k(DATEI.fotoKuehlungNachher, "belongs_to_job", AUFTRAG.kuehlung, PERSON.monteurA, 0),
    k(DATEI.aufmassTreppenhaus, "belongs_to_job", AUFTRAG.treppenhaus, PERSON.buero, -21),
    k(DATEI.planWallbox, "belongs_to_job", AUFTRAG.wallbox, PERSON.buero, -14),
    k(DOKUMENT.pruefprotokoll2025, "belongs_to_job", AUFTRAG.hebebuehne, PERSON.inhaber, -6),
    k(DOKUMENT.lieferschein, "belongs_to_job", AUFTRAG.hallenbeleuchtung, PERSON.buero, -1),

    // Anhänge an ihrer Nachricht
    k(DOKUMENT.rechnungElektrogrosshandel, "attachment_of",
      MAIL.rechnungElektrogrosshandel, PERSON.buero, 0),
    k(DOKUMENT.rechnungKabelwerk, "attachment_of", MAIL.rechnungKabelwerk, PERSON.buero, -1),
    k(DOKUMENT.rechnungFehlerhaft, "attachment_of", MAIL.rechnungFehlerhaft, PERSON.buero, -1),

    // Eingangsvorgänge und ihre Quelle
    k(EINGANG.rechnungElektrogrosshandel, "received_via",
      MAIL.rechnungElektrogrosshandel, PERSON.buero, 0),
    k(EINGANG.reklamationBaeckerei, "received_via", MAIL.reklamationBaeckerei, PERSON.buero, -3),
    k(EINGANG.reklamationBaeckerei, "belongs_to_job", AUFTRAG.kuehlung, PERSON.buero, -3),
    k(EINGANG.lieferschein, "belongs_to_job", AUFTRAG.hallenbeleuchtung, PERSON.buero, -1),

    // Mail am Auftrag. Ohne diese Kante fehlt der Auftragsakte ihre
    // Kommunikationsspur (`07` §7.5) — sie waere nur ueber den Umweg des
    // Eingangsvorgangs erreichbar, und ausgehende Mails haben gar keinen.
    k(MAIL.reklamationBaeckerei, "mentioned_in", AUFTRAG.kuehlung, PERSON.buero, -3),
    k(MAIL.angebotWallbox, "mentioned_in", AUFTRAG.wallbox, PERSON.buero, -14),
    k(MAIL.rueckfrageHausverwaltung, "mentioned_in", AUFTRAG.zaehlerschrank, PERSON.buero, -3),
    k(MAIL.wartungsvertragEntwurf, "mentioned_in", AUFTRAG.rufanlage, PERSON.inhaber, -30),

    // Termine am Auftrag — `07` §7.5 fuehrt "Termine und Kontakte" als eigenen
    // Bereich der Akte.
    k(TERMIN.praxisBesprechung, "scheduled_from", AUFTRAG.praxisumbau, PERSON.buero, -2),
    k(TERMIN.abnahmeWallbox, "scheduled_from", AUFTRAG.wallbox, PERSON.buero, -1),

    // Chat und Termin am Auftrag
    k(CHATRAUM.kuehlung, "belongs_to_job", AUFTRAG.kuehlung, PERSON.buero, -9),
    k(CHATRAUM.stoerung, "belongs_to_job", AUFTRAG.stoerung, PERSON.buero, 0),
    k(CHATRAUM.wallbox, "belongs_to_job", AUFTRAG.wallbox, PERSON.buero, -14),

    // Berichte
    k(BERICHT.kuehlungV1, "report_for", EINSATZ.kuehlungVorwoche, PERSON.monteurA, -7),
    k(BERICHT.kuehlungV2, "report_for", EINSATZ.kuehlungHeute, PERSON.monteurA, 0),
    k(BERICHT.kuehlungV2, "supersedes", BERICHT.kuehlungV1, PERSON.monteurA, 0),
    k(BERICHT.treppenhaus, "report_for", EINSATZ.treppenhausVorwoche, PERSON.monteurB, -5),
    k(DATEI.berichtPdfKuehlung, "published_from", BERICHT.kuehlungV1, PERSON.buero, -6),
    k(DATEI.berichtPdfTreppenhaus, "published_from", BERICHT.treppenhaus, PERSON.buero, -4),

    // MagicLinks entstehen aus einer Ressource
    k(MAGICLINK.berichtBaeckerei, "generated_from", DATEI.berichtPdfKuehlung, PERSON.buero, -6),
    k(MAGICLINK.abnahmeAutohaus, "generated_from",
      DOKUMENT.pruefprotokoll2025, PERSON.inhaber, -1),

    // Wissen, das in einem Chatbeitrag erwähnt wurde
    k(ref("nextcloud", "collective-page", "w-01c01"), "mentioned_in",
      ref("nextcloud", "talk-message", "tm-4005"), PERSON.buero, 0),

    // Folgeeinsätze
    k(EINSATZ.kuehlungHeute, "related_to", EINSATZ.kuehlungVorwoche, PERSON.buero, -3),
    k(EINSATZ.wallboxMorgen, "related_to", EINSATZ.wallboxHeute, PERSON.buero, -14),
  ];
}

/**
 * Die Auditspur — `05` §5.15 stellt jeden Statuswechsel unter Auditpflicht.
 *
 * Der wichtigste Eintrag ist der stellvertretende: ADR-045 erlaubt dem Büro,
 * die Übergänge des Monteurs zu setzen — „er ruft an, statt zu tippen, und bei
 * schlechtem Empfang ist das der Normalfall". Der Preis ist die Urheberschaft,
 * und deshalb protokolliert das Audit **beide**. Ein Bestand ohne diesen Fall
 * würde die Anzeige nie zwingen, zwei Namen darzustellen.
 */
export function audit(bezugstag: Date): readonly Auditeintrag[] {
  const a = (
    id: string,
    tageVersatz: number,
    stunde: number,
    minute: number,
    objekt: Auditeintrag["objekt"],
    handlung: string,
    akteur: Auditeintrag["akteur"],
    von?: string,
    nach?: string,
    imAuftragVon?: Auditeintrag["imAuftragVon"],
  ): Auditeintrag => ({
    ref: ref("portal", "audit-entry", id),
    zeitpunkt: zeitpunkt(bezugstag, tageVersatz, stunde, minute),
    objekt,
    handlung,
    akteur,
    ...(von ? { von } : {}),
    ...(nach ? { nach } : {}),
    ...(imAuftragVon ? { imAuftragVon } : {}),
  });

  return [
    a("au-3001", 0, 6, 45, EINSATZ.stoerungHeute, "Einsatz angelegt (Notdienst)",
      PERSON.buero),
    a("au-3002", 0, 6, 50, EINSATZ.stoerungHeute, "Zustand gewechselt",
      PERSON.monteurA, "geplant", "anfahrt"),
    a("au-3003", 0, 7, 2, EINSATZ.stoerungHeute, "Zustand gewechselt",
      PERSON.monteurA, "anfahrt", "vor-ort"),
    a("au-3004", 0, 7, 51, EINSATZ.stoerungHeute, "Zustand gewechselt",
      PERSON.monteurA, "vor-ort", "abschluss"),
    a("au-3005", 0, 7, 55, EINSATZ.stoerungHeute, "Zustand gewechselt",
      PERSON.monteurA, "abschluss", "uebermittelt"),
    a("au-3006", 0, 7, 58, EINSATZ.kuehlungHeute, "Zustand gewechselt",
      PERSON.monteurA, "geplant", "anfahrt"),
    a("au-3007", 0, 8, 12, EINSATZ.kuehlungHeute, "Zustand gewechselt",
      PERSON.monteurA, "anfahrt", "vor-ort"),
    a("au-3008", 0, 14, 40, EINSATZ.hebebuehneHeute, "Zustand gewechselt",
      PERSON.monteurB, "vor-ort", "wartet"),
    // Der stellvertretende Wechsel: Das Büro schaltet, der Monteur ist gemeint.
    a("au-3009", 0, 9, 2, EINSATZ.treppenhausHeute, "Zustand stellvertretend gewechselt",
      PERSON.buero, "geplant", "anfahrt", PERSON.monteurB),
    a("au-3010", -2, 7, 55, EINSATZ.hallenbeleuchtungStorniert, "Einsatz storniert",
      PERSON.buero, "anfahrt", "storniert"),
    a("au-3011", -4, 16, 40, EINSATZ.hebebuehneVorwoche, "Vom Büro zurückgegeben",
      PERSON.inhaber, "uebermittelt", "abschluss"),
    a("au-3012", -6, 9, 5, MAGICLINK.berichtBaeckerei, "MagicLink erstellt", PERSON.buero),
    a("au-3013", -5, 10, 0, MAGICLINK.angebotMetallbau, "MagicLink widerrufen", PERSON.buero),
    a("au-3014", -2, 3, 20, EINGANG.verdaechtigerAnhang,
      "Eingangsvorgang in Quarantäne gestellt", ref("portal", "service", "eingangspruefung")),
    a("au-3015", -6, 9, 0, BERICHT.kuehlungV1, "Servicebericht freigegeben",
      PERSON.buero, "zur_pruefung", "freigegeben"),
  ];
}
